import { Fragment, useRef, useState } from 'react';
import { Check, Upload } from 'lucide-react';
import { uploadAttachment } from '../../../lib/attachments';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useRewardRules } from '../../../lib/use-reward-rules';
import { claimReasonKey, CLAIM_FAILURE_KEY, type NetworkRow, type SocialNetwork } from '../../../lib/social-rewards';
import { Badge, Button, Card, SocialLogo, Typography } from '../../symphony/ui';
import { SETTINGS_PAGE } from './settings-shared';
import styles from './EarnCreditsSection.module.css';

// The permanent "Earn credits" section of the share screen, rendered inside BOTH share layouts (the
// desktop modal and the full-screen phone one). Follow us, post about the product with your invite
// link, upload a screenshot — an AI check verifies the post and the reward lands immediately.
//
// Every amount here is SERVED (`useRewardRules` → /api/reward-rules); nothing is hard-coded, and a
// failed rules read renders the no-amount copy rather than a number that may have changed.
//
// This section is PROGRAM 2 and nothing else. The referral sentence (`settings.earnCredits.referralNote`
// — "…credits every time a friend joins with your link…") used to close it, which put referral copy
// inside the social block and made the share screen read as one mixed-up offer; it now lives with the
// rest of the referral program in the two share layouts. Keep referral copy OUT of here.

type ClaimState =
  | { kind: 'idle' }
  | { kind: 'claiming' }
  | { kind: 'done' }
  | { kind: 'error'; messageKey: string };

export function EarnCreditsSection({ open, className }: { open: boolean; className?: string }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi, logLoadStart, logLoadFinish } = useGenericBi();
  const { networks, socialReward, loading, error, refresh } = useRewardRules({ enabled: open });
  useLoadBi('earn-credits-rules', { isLoading: loading, isError: error });

  const [selected, setSelected] = useState<SocialNetwork | null>(null);
  const [claim, setClaim] = useState<ClaimState>({ kind: 'idle' });
  const fileInput = useRef<HTMLInputElement | null>(null);

  const pick = (row: NetworkRow) => {
    if (row.claimed) return;
    logPageClickBi(
      { ...SETTINGS_PAGE, button_name: 'earn_credits_network' },
      { componentName: `earn-credits-${row.network.toLowerCase()}` },
    );
    setSelected(row.network === selected ? null : row.network);
    setClaim({ kind: 'idle' });
  };

  const submit = async (file: File) => {
    const network = selected;
    if (!network) return;
    logPageClickBi(
      { ...SETTINGS_PAGE, button_name: 'earn_credits_claim' },
      { componentName: 'earn-credits-claim' },
    );
    setClaim({ kind: 'claiming' });

    // The claim is a measured load transaction, matching what both native surfaces already report
    // (`earn_credits_claim` loadStart → loadFinish with isSuccessful + the rejection reason). Web fired
    // only the two clicks, so its pass/fail rate and rejection mix were invisible in Grafana while
    // native's were queryable — the funnel could not be joined across surfaces.
    const startedAt = Date.now();
    const finish = (isSuccessful: boolean, reason?: string) =>
      logLoadFinish(
        'earn-credits-claim',
        { isSuccessful, loadingTimeMs: Date.now() - startedAt },
        { additionalFields: reason ? { network, reason } : { network } },
      );
    logLoadStart('earn-credits-claim', { additionalFields: { network } });

    const upload = await uploadAttachment(file, { imageOnly: true });
    if (!upload.ok) {
      setClaim({ kind: 'error', messageKey: CLAIM_FAILURE_KEY });
      finish(false, 'upload_failed');
      return;
    }

    try {
      const res = await apiFetch(`${API_BASE}/api/social-reward-claim`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ network, screenshotUrl: upload.attachment.url }),
      });
      const body = res.ok ? ((await res.json()) as { ok?: boolean; reason?: string }) : null;
      if (!body?.ok) {
        setClaim({ kind: 'error', messageKey: claimReasonKey(body?.reason) });
        finish(false, body?.reason ?? 'request_failed');
        return;
      }
    } catch {
      setClaim({ kind: 'error', messageKey: CLAIM_FAILURE_KEY });
      finish(false, 'request_failed');
      return;
    }

    setClaim({ kind: 'done' });
    finish(true);
    // The claimed list is the server's, so re-read rather than patching a network into it locally.
    refresh();
  };

  const selectedRow = networks.find((row) => row.network === selected) ?? null;
  const claiming = claim.kind === 'claiming';

  // Nothing served yet → nothing to show, exactly as both native surfaces already behave (iOS
  // `if !networks.isEmpty`, Android `if (networks.isEmpty()) return`). The section is entirely
  // server-driven — which networks exist, what they are called, whose handle to follow — so there is
  // no offline shape to render, and a heading over an empty list promises a program the caller cannot
  // reach. It also OWNS the separator above it: the divider that now splits the two reward programs is
  // drawn by this section's own top border, so when the social program is absent the line goes with it
  // rather than hanging under the referral block as a stray rule.
  if (networks.length === 0) return null;

  return (
    <section className={`${styles.section} ${className ?? ''}`}>
      <div className={styles.head}>
        <Typography variant="h4" component="h3">
          {t('settings.earnCredits.heading', { productName })}
        </Typography>
        <Typography variant="p2" component="p" color="secondary">
          {socialReward
            ? t('settings.earnCredits.subtitle', {
                productName,
                credits: socialReward.credits,
                agents: socialReward.subAgentRuns,
                briefs: socialReward.agentMeetings,
              })
            : t('settings.earnCredits.subtitleNoAmount', { productName })}
        </Typography>
      </div>

      <div className={styles.rows}>
        {networks.map((row) => (
          <Fragment key={row.network}>
          <Card
            variant={row.claimed ? 'stone' : 'sunken'}
            size="sm"
            className={styles.row}
            interactive={!row.claimed}
            selected={row.network === selected}
            component={row.claimed ? 'div' : 'button'}
            aria-pressed={row.claimed ? undefined : row.network === selected}
            aria-expanded={row.claimed ? undefined : row.network === selected}
            aria-controls={row.claimed ? undefined : `earn-credits-steps-${row.network}`}
            onClick={() => pick(row)}
          >
            {/* The mark rides a white plate so the near-black TikTok and X glyphs stay legible in dark
                mode — the same plate treatment the connectors genome gives a connected badge. */}
            <span className={styles.logo} aria-hidden>
              <SocialLogo network={row.network} />
            </span>
            <span className={styles.rowText}>
              <Typography variant="h4" component="span">
                {row.displayName}
              </Typography>
              {row.officialHandle && (
                <Typography variant="p3" component="span" color="secondary">
                  {row.officialHandle}
                </Typography>
              )}
            </span>
            {row.claimed && (
              <Badge
                variant="done"
                label={t('settings.earnCredits.claimed')}
                startIcon={<Check size={16} aria-hidden />}
              />
            )}
          </Card>

          {/* The playbook expands INLINE, directly under the row that was tapped — an accordion, exactly
              as iOS renders it (`if isOpen { playbook(n) }` inside `networkRow`) and Android alongside it.
              It used to render after the whole list: with six networks the panel landed below the fold of
              a scrolling modal, so selecting a network looked like a dead click. Feedback belongs next to
              the control that produced it. It is a SIBLING of the Card, never a child — an unclaimed Card
              renders as a button element, and nesting the upload control inside one is invalid HTML. */}
          {selectedRow?.network === row.network && (
        <div className={styles.steps} id={`earn-credits-steps-${row.network}`}>
          <Typography variant="p2" component="p">
            {t('settings.earnCredits.step1', { network: selectedRow.displayName, handle: selectedRow.officialHandle })}
          </Typography>
          <Typography variant="p2" component="p">
            {t(selectedRow.requiresLink ? 'settings.earnCredits.step2' : 'settings.earnCredits.step2NoLink', {
              productName,
            })}
          </Typography>
          <Typography variant="p2" component="p">
            {t('settings.earnCredits.step3')}
          </Typography>

          {/* A file picker has no DS face of its own — the DS Button IS the control, and the input is
              the browser plumbing behind it (it is not a text field, so the Input register is silent). */}
          <input
            ref={fileInput}
            className={styles.file}
            type="file"
            accept="image/*"
            aria-label={t('settings.earnCredits.upload')}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void submit(file);
            }}
          />
          <Button
            size="medium"
            loading={claiming}
            disabled={claiming}
            startIcon={<Upload size={16} aria-hidden />}
            onClick={() => fileInput.current?.click()}
          >
            {claiming ? t('settings.earnCredits.checking') : t('settings.earnCredits.upload')}
          </Button>

          {claim.kind === 'done' && (
            <Typography variant="p2" component="p" role="status">
              {t('settings.earnCredits.success')}
            </Typography>
          )}
          {claim.kind === 'error' && (
            <Typography variant="p2" component="p" role="alert" color="secondary">
              {t(claim.messageKey, { productName })}
            </Typography>
          )}
        </div>
          )}
          </Fragment>
        ))}
      </div>
    </section>
  );
}
